/**
 * Human Handoff Dashboard Component
 * View and manage human handoff requests from AI agents
 */

import React, { useState, useEffect } from 'react';
import { orchestratorApi } from '../api/orchestrator';
import './GoogleCalendarAuth.css'; // Reuse styles

type HandoffStatus = 'pending' | 'assigned' | 'in_progress' | 'resolved' | 'cancelled' | 'escalated';
type HandoffPriority = 'low' | 'medium' | 'high' | 'urgent';

interface HandoffTask {
  id: string;
  tenant_id: string;
  entity_id: string;
  conversation_id?: string;
  chat_id?: string;
  phone_number?: string;
  reason: string;
  priority: HandoffPriority;
  context?: Record<string, any>;
  status: HandoffStatus;
  assigned_to?: string;
  assigned_at?: string;
  resolution_notes?: string;
  resolved_at?: string;
  created_at: string;
  updated_at: string;
}

interface HandoffStats {
  by_status: Record<string, number>;
  by_priority: Record<string, number>;
  total: number;
  avg_resolution_minutes: number;
}

const STATUS_LABELS: Record<HandoffStatus, string> = {
  pending: 'Pending',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  cancelled: 'Cancelled',
  escalated: 'Escalated',
};

const STATUS_COLORS: Record<HandoffStatus, string> = {
  pending: '#f59e0b',
  assigned: '#3b82f6',
  in_progress: '#8b5cf6',
  resolved: '#10b981',
  cancelled: '#6b7280',
  escalated: '#ef4444',
};

const PRIORITY_LABELS: Record<HandoffPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

const PRIORITY_COLORS: Record<HandoffPriority, string> = {
  low: '#10b981',
  medium: '#f59e0b',
  high: '#ef9744',
  urgent: '#ef4444',
};

export const HumanHandoffDashboard: React.FC = () => {
  // State
  const [tasks, setTasks] = useState<HandoffTask[]>([]);
  const [stats, setStats] = useState<HandoffStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<HandoffStatus | 'all'>('all');
  const [filterPriority, setFilterPriority] = useState<HandoffPriority | 'all'>('all');
  const [selectedTask, setSelectedTask] = useState<HandoffTask | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);

  // Update form
  const [updateForm, setUpdateForm] = useState({
    status: '',
    assigned_to: '',
    resolution_notes: '',
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadStats();
    loadTasks();
  }, [filterStatus, filterPriority, page]);

  const loadStats = async () => {
    try {
      const response = await fetch('/api/handoff/stats', {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to load handoff stats:', error);
    }
  };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (page * limit).toString(),
      });

      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }

      if (filterPriority !== 'all') {
        params.append('priority', filterPriority);
      }

      const response = await fetch(`/api/handoff/tasks?${params}`, {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        setTasks(data.tasks);
        setTotal(data.total);
      } else {
        showMessage('error', data.error || 'Failed to load tasks');
      }
    } catch (error) {
      console.error('Failed to load handoff tasks:', error);
      showMessage('error', 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTask = async () => {
    if (!selectedTask) return;

    try {
      const response = await fetch(`/api/handoff/tasks/${selectedTask.id}/status`, {
        method: 'POST',
        headers: {
          ...(await orchestratorApi.getAuthHeaders()),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateForm),
      });

      const data = await response.json();

      if (data.ok) {
        showMessage('success', 'Handoff task updated successfully');
        setSelectedTask(data.task);
        await loadTasks();
        await loadStats();
      } else {
        showMessage('error', data.error || 'Failed to update task');
      }
    } catch (error) {
      console.error('Failed to update handoff task:', error);
      showMessage('error', 'Failed to update task');
    }
  };

  const showMessage = (type: 'success' | 'error' | 'info', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const getAge = (createdAt: string): string => {
    const diff = Date.now() - new Date(createdAt).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d`;
    if (hours > 0) return `${hours}h`;
    if (minutes > 0) return `${minutes}m`;
    return 'Just now';
  };

  return (
    <div className="google-calendar-auth">
      <div className="gca-header">
        <h2>👥 Human Handoff Dashboard</h2>
        <p className="gca-description">
          Monitor and manage requests from AI agents to transfer conversations to human agents.
          Track assignment status, resolution time, and priority levels.
        </p>
      </div>

      {message && (
        <div className={`gca-message gca-message-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Statistics Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginBottom: '20px' }}>
          <div className="gca-card" style={{ padding: '15px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3b82f6' }}>{stats.total}</div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>Total Requests</div>
          </div>
          <div className="gca-card" style={{ padding: '15px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b' }}>{stats.by_status.pending || 0}</div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>Pending</div>
          </div>
          <div className="gca-card" style={{ padding: '15px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#10b981' }}>{stats.avg_resolution_minutes.toFixed(0)}m</div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>Avg Resolution</div>
          </div>
          <div className="gca-card" style={{ padding: '15px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ef4444' }}>{stats.by_priority.urgent || 0}</div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>Urgent</div>
          </div>
        </div>
      )}

      {/* By Status */}
      {stats && (
        <div className="gca-card" style={{ marginBottom: '20px' }}>
          <h3>Requests by Status</h3>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {Object.entries(stats.by_status).map(([status, count]) => (
              <div
                key={status}
                style={{
                  padding: '10px 15px',
                  borderRadius: '6px',
                  border: `1px solid ${STATUS_COLORS[status as HandoffStatus]}`,
                  backgroundColor: STATUS_COLORS[status as HandoffStatus] + '10',
                  cursor: 'pointer',
                }}
                onClick={() => setFilterStatus(filterStatus === status ? 'all' : status as HandoffStatus)}
              >
                <span style={{ fontWeight: 'bold', color: STATUS_COLORS[status as HandoffStatus] }}>{count}</span>
                {' '}{STATUS_LABELS[status as HandoffStatus]}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="gca-card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <span>Filter:</span>
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value as HandoffStatus | 'all'); setPage(0); }}
            style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="assigned">Assigned</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="cancelled">Cancelled</option>
            <option value="escalated">Escalated</option>
          </select>

          <select
            value={filterPriority}
            onChange={(e) => { setFilterPriority(e.target.value as HandoffPriority | 'all'); setPage(0); }}
            style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          >
            <option value="all">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>

          <button
            onClick={() => { setFilterStatus('all'); setFilterPriority('all'); setPage(0); }}
            className="gca-button gca-button-small"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Tasks Table */}
      <div className="gca-card">
        <h3>Handoff Requests {filterStatus !== 'all' && `(${STATUS_LABELS[filterStatus]})`}</h3>

        {loading ? (
          <p>Loading...</p>
        ) : tasks.length === 0 ? (
          <p className="gca-empty">No handoff requests found.</p>
        ) : (
          <table className="gca-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Reason</th>
                <th>Contact</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Age</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <tr
                  key={task.id}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedTask?.id === task.id ? '#eff6ff' : undefined,
                  }}
                  onClick={() => {
                    setSelectedTask(task);
                    setUpdateForm({
                      status: task.status,
                      assigned_to: task.assigned_to || '',
                      resolution_notes: task.resolution_notes || '',
                    });
                  }}
                >
                  <td>
                    <span
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: PRIORITY_COLORS[task.priority] + '20',
                        color: PRIORITY_COLORS[task.priority],
                      }}
                    >
                      {PRIORITY_LABELS[task.priority]}
                    </span>
                  </td>
                  <td style={{ maxWidth: '200px' }}>{task.reason}</td>
                  <td>
                    {task.phone_number || task.chat_id || '-'}
                  </td>
                  <td>
                    <span
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: STATUS_COLORS[task.status] + '20',
                        color: STATUS_COLORS[task.status],
                      }}
                    >
                      {STATUS_LABELS[task.status]}
                    </span>
                  </td>
                  <td>{task.assigned_to || '-'}</td>
                  <td style={{ fontSize: '12px', color: '#6b7280' }}>
                    {getAge(task.created_at)}
                  </td>
                  <td>
                    <button
                      className="gca-button gca-button-small"
                      onClick={(e) => { e.stopPropagation(); setSelectedTask(task); }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {total > limit && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="gca-button gca-button-small"
            >
              Previous
            </button>
            <span style={{ padding: '10px' }}>
              Page {page + 1} of {Math.ceil(total / limit)}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={(page + 1) * limit >= total}
              className="gca-button gca-button-small"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setSelectedTask(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>Handoff Request Details</h2>
              <button
                onClick={() => setSelectedTask(null)}
                style={{ border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gap: '15px' }}>
              {/* Priority & Status */}
              <div style={{ display: 'flex', gap: '20px' }}>
                <div>
                  <strong>Priority:</strong>{' '}
                  <span style={{ color: PRIORITY_COLORS[selectedTask.priority] }}>
                    {PRIORITY_LABELS[selectedTask.priority]}
                  </span>
                </div>
                <div>
                  <strong>Status:</strong>{' '}
                  <span style={{ color: STATUS_COLORS[selectedTask.status] }}>
                    {STATUS_LABELS[selectedTask.status]}
                  </span>
                </div>
              </div>

              {/* Reason */}
              <div>
                <strong>Reason:</strong>
                <p style={{ marginTop: '5px', padding: '10px', backgroundColor: '#fef3c7', borderRadius: '4px' }}>
                  {selectedTask.reason}
                </p>
              </div>

              {/* Contact Info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {selectedTask.phone_number && (
                  <div><strong>Phone:</strong> {selectedTask.phone_number}</div>
                )}
                {selectedTask.chat_id && (
                  <div><strong>Chat ID:</strong> {selectedTask.chat_id}</div>
                )}
                <div><strong>Entity:</strong> {selectedTask.entity_id}</div>
                {selectedTask.conversation_id && (
                  <div><strong>Conversation:</strong> {selectedTask.conversation_id}</div>
                )}
              </div>

              {/* Context */}
              {selectedTask.context && Object.keys(selectedTask.context).length > 0 && (
                <div>
                  <strong>Context:</strong>
                  <pre style={{
                    marginTop: '5px',
                    padding: '10px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '4px',
                    overflow: 'auto',
                    maxHeight: '150px',
                  }}>
                    {JSON.stringify(selectedTask.context, null, 2)}
                  </pre>
                </div>
              )}

              {/* Timestamps */}
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                <div>Created: {new Date(selectedTask.created_at).toLocaleString()}</div>
                {selectedTask.assigned_at && (
                  <div>Assigned: {new Date(selectedTask.assigned_at).toLocaleString()}</div>
                )}
                {selectedTask.resolved_at && (
                  <div>Resolved: {new Date(selectedTask.resolved_at).toLocaleString()}</div>
                )}
              </div>

              {/* Update Form */}
              <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                <h3>Update Status</h3>
                <div style={{ display: 'grid', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Status</label>
                    <select
                      value={updateForm.status}
                      onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value })}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                      <option value="">Select status...</option>
                      <option value="pending">Pending</option>
                      <option value="assigned">Assigned</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="escalated">Escalated</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Assigned To</label>
                    <input
                      type="text"
                      value={updateForm.assigned_to}
                      onChange={(e) => setUpdateForm({ ...updateForm, assigned_to: e.target.value })}
                      placeholder="Enter agent name or email"
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Resolution Notes</label>
                    <textarea
                      value={updateForm.resolution_notes}
                      onChange={(e) => setUpdateForm({ ...updateForm, resolution_notes: e.target.value })}
                      placeholder="Add resolution notes..."
                      rows={3}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>

                  {selectedTask.resolution_notes && (
                    <div>
                      <strong>Previous Notes:</strong>
                      <p style={{ marginTop: '5px', padding: '10px', backgroundColor: '#f3f4f6', borderRadius: '4px' }}>
                        {selectedTask.resolution_notes}
                      </p>
                    </div>
                  )}
                </div>

                <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                  <button
                    onClick={handleUpdateTask}
                    disabled={!updateForm.status}
                    className="gca-button gca-button-primary"
                  >
                    Update Task
                  </button>
                  <button
                    onClick={() => setSelectedTask(null)}
                    className="gca-button"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="gca-card gca-info">
        <h3>ℹ️ About Human Handoff</h3>
        <p className="gca-subtext">
          AI agents can request human assistance when they encounter situations beyond their capabilities,
          such as complex issues, emotional customers, or sensitive matters. The handoff dashboard helps
          your team manage these requests efficiently.
        </p>
        <div className="gca-steps">
          <strong>Priority Levels:</strong>
          <ul style={{ marginTop: '10px' }}>
            <li><strong>Low:</strong> General inquiry or non-urgent request</li>
            <li><strong>Medium:</strong> Customer needs assistance beyond AI capabilities</li>
            <li><strong>High:</strong> Complex issue or frustrated customer</li>
            <li><strong>Urgent:</strong> Critical issue requiring immediate attention</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default HumanHandoffDashboard;
